import axios from 'axios';
import type { NavigateFunction } from 'react-router-dom';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { SERVER_API_ENDPOINT } from 'constants/envs';
import { getRoleValues, hasAdminAccess, resetCurrentUserCache } from 'hooks/use-current-roles';
import { StorageKey, StorageUtils } from 'utils/local-storage';
import { AppDispatch } from '../../types/redux';
import { setSession } from '../../utils/session';
import {
  AuthError,
  getAuthFailure,
  getAuthLoading,
  getAuthSuccess,
  removeAuth,
  setAuthPhase,
} from './reducers';

/** Body của POST auth/login: LocalStrategy của backend đọc field `username` (là email). */
export type LoginPayload = {
  username: string;
  password: string;
};

/** Thông báo trang Đăng nhập hiện khi được điều hướng về (location.state.notice). */
export type LoginNotice = 'loggedOut' | 'passwordChanged' | 'notAdmin';

export type LoginResult = 'ok' | 'error' | 'denied';

/**
 * Huỷ phiên trên server, KHÔNG BAO GIỜ ném lỗi. Gọi thẳng axios (bỏ qua interceptor của
 * AxiosUtils): phiên đã hết thì auth/logout trả 403 và interceptor sẽ tải lại trang về /login,
 * chen ngang điều hướng đang làm.
 */
export async function safeLogout(): Promise<void> {
  try {
    await axios.post(API.logout, null, {
      baseURL: SERVER_API_ENDPOINT,
      withCredentials: true,
      timeout: 8000,
    });
  } catch {
    // Phiên đã hết / mất mạng: phía trình duyệt vẫn xoá phiên như thường.
  }
}

const clearStoredSession = () => {
  try {
    StorageUtils.remove(StorageKey.SESSION);
  } catch {
    // localStorage bị chặn: không có gì để xoá.
  }
};

/** Chuẩn hoá lỗi interceptor ném ra (object NestJS, object mất mạng, hoặc chuỗi HTML của proxy). */
const toAuthError = (error: unknown): AuthError => {
  if (error && typeof error === 'object') {
    const e = error as Record<string, any>;
    return {
      statusCode: typeof e.statusCode === 'number' ? e.statusCode : undefined,
      message: e.message,
      networkError: !!e.networkError,
      path: e.path,
      timestamp: e.timestamp,
    };
  }
  // Proxy / máy chủ trả trang HTML thay vì JSON: coi như lỗi máy chủ.
  return { statusCode: 500, message: typeof error === 'string' ? error.slice(0, 200) : undefined };
};

/**
 * Đăng nhập + chặn theo vai trò (đặc tả §5, lớp 1).
 * 1. POST auth/login. 2. GET auth/status đọc vai trò.
 * 3. Có ADMIN/MOD → lưu phiên, về "/". Không có → POST auth/logout, KHÔNG lưu phiên,
 *    error.reason = 'NOT_ADMIN'. auth/status lỗi → logout, reason = 'ROLE_CHECK_FAILED'.
 *
 * Lưu ý: backend đã xoá mọi phiên + device token khác của tài khoản ngay khi mật khẩu đúng
 * (auth.service.ts), tức là TRƯỚC bước kiểm tra vai trò ở đây.
 */
export const login =
  (account: LoginPayload, navigate: NavigateFunction) =>
  async (dispatch: AppDispatch): Promise<LoginResult> => {
    dispatch(getAuthLoading());

    let res: any;
    try {
      res = await AxiosUtils.post(API.login, account);
    } catch (error) {
      dispatch(getAuthFailure(toAuthError(error)));
      return 'error';
    }

    dispatch(setAuthPhase('checking'));
    let roles: string[];
    try {
      roles = getRoleValues(await AxiosUtils.get(API.getUserInfo));
    } catch {
      await safeLogout();
      clearStoredSession();
      resetCurrentUserCache();
      dispatch(getAuthFailure({ reason: 'ROLE_CHECK_FAILED' }));
      return 'denied';
    }

    if (!hasAdminAccess(roles)) {
      await safeLogout();
      clearStoredSession();
      resetCurrentUserCache();
      dispatch(getAuthFailure({ reason: 'NOT_ADMIN' }));
      return 'denied';
    }

    resetCurrentUserCache();
    setSession({ accessToken: res?.data });
    dispatch(getAuthSuccess({ accessToken: res?.data }));
    navigate('/', { replace: true });
    return 'ok';
  };

/**
 * Đăng xuất thật: huỷ phiên trên server rồi mới xoá phía trình duyệt, về /login kèm thông báo.
 * Dùng cho nút Đăng xuất (notice 'loggedOut') và AccessGate khi vai trò bị gỡ ('notAdmin').
 */
export const logout =
  (navigate: NavigateFunction, notice: LoginNotice = 'loggedOut') =>
  async (dispatch: AppDispatch): Promise<void> => {
    await safeLogout();
    try {
      // Như trước: xoá cả tuỳ chọn cục bộ (máy dùng chung không lộ bộ lọc của người trước).
      localStorage.clear();
    } catch {
      // localStorage bị chặn: bỏ qua.
    }
    resetCurrentUserCache();
    dispatch(removeAuth());
    navigate('/login', { replace: true, state: { notice } });
  };
